/**
 * Features released in stages (Admin › Releases): what this account may use.
 *
 *   admin          platform admins, always
 *   beta           an account picked for the beta
 *   live           released, and the plan includes it
 *   plan_required  released, but not on this plan
 *   soon           not released to this account yet: shown with "Coming soon"
 *
 * One read per account (my_feature_access). Before the migration is applied
 * the function does not exist: admins get everything, everyone else "soon".
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ReleaseFeature = "connected_apps" | "suggestion_mockup" | "ai_agent" | "user_agent" | "funnel" | "figma_design" | "free_trial" | "live_heatmaps" | "ab_testing";
export type FeatureAccess = "admin" | "beta" | "live" | "plan_required" | "soon";
export type FeatureAccessMap = Record<ReleaseFeature, FeatureAccess>;

export const RELEASE_FEATURES: readonly ReleaseFeature[] = ["connected_apps", "suggestion_mockup", "ai_agent", "user_agent", "funnel", "figma_design", "free_trial", "live_heatmaps", "ab_testing"];
const ACCESS: readonly FeatureAccess[] = ["admin", "beta", "live", "plan_required", "soon"];

export const canUse = (a: FeatureAccess | null | undefined): boolean => a === "admin" || a === "beta" || a === "live";

const all = (a: FeatureAccess): FeatureAccessMap => Object.fromEntries(RELEASE_FEATURES.map((k) => [k, a])) as FeatureAccessMap;

/** Every key in, unknown values out: what the server sent, made safe to read. */
export function readAccessMap(raw: unknown): FeatureAccessMap {
  const out = all("soon");
  if (raw && typeof raw === "object") {
    for (const k of RELEASE_FEATURES) {
      const v = (raw as Record<string, unknown>)[k];
      if (ACCESS.includes(v as FeatureAccess)) out[k] = v as FeatureAccess;
    }
  }
  return out;
}

export function useFeatureAccess(accountId: string | null | undefined) {
  return useQuery({
    queryKey: ["feature-access", accountId ?? null],
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<FeatureAccessMap> => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase.rpc as any)("my_feature_access", { p_account: accountId ?? null });
        if (!error && data) return readAccessMap(data);
      } catch { /* not deployed yet */ }
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return all("soon");
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
      return all(data ? "admin" : "soon");
    },
  });
}

/** The label a feature carries where it is shown: nothing when it is simply live. */
export function accessBadge(a: FeatureAccess | null | undefined): "Beta" | "Coming soon" | null {
  if (a === "beta") return "Beta";
  if (a === "soon" || a == null) return "Coming soon";
  return null;
}

/** Plan features held back until their release is live (and before the stages have been read). */
export const STAGED_PLAN_FEATURES: ReadonlySet<string> = new Set(["connectedApps", "aiAgent", "funnelAnalysis", "userAgentMode", "figmaAnalysis"]);

/**
 * Plan feature keys (features_library) whose release is not live yet. Pricing
 * and plan screens leave them out entirely until Admin › Releases sets them
 * Live. Readable without an account.
 */
export function useUnreleasedPlanFeatures() {
  return useQuery({
    queryKey: ["feature-release-stages"],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<Set<string>> => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase.rpc as any)("feature_release_stages");
        if (!error && Array.isArray(data)) {
          return new Set(
            (data as { stage?: string; plan_feature_key?: string | null }[])
              .filter((r) => r.stage !== "live" && r.plan_feature_key)
              .map((r) => String(r.plan_feature_key)),
          );
        }
      } catch { /* not deployed yet */ }
      return new Set(STAGED_PLAN_FEATURES);
    },
  });
}

/** The unreleased set to filter by: held back until the stages are read, so nothing flashes in and out. */
export function useHiddenPlanFeatures(): ReadonlySet<string> {
  const { data } = useUnreleasedPlanFeatures();
  return data ?? STAGED_PLAN_FEATURES;
}

/**
 * Release keys that are live for everyone, for public pages: the site menu
 * shows a staged item only once it is live. Empty until read, so the
 * server-rendered menu and the first client render agree.
 */
export function useLiveReleases() {
  return useQuery({
    queryKey: ["feature-release-live"],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<Set<string>> => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase.rpc as any)("feature_release_stages");
        if (!error && Array.isArray(data)) return new Set((data as { key: string; stage: string }[]).filter((r) => r.stage === "live").map((r) => r.key));
      } catch { /* not deployed yet */ }
      return new Set<string>();
    },
  });
}
